import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { locationService } from '../services/locationService';
import './CustomerSignUp.css';

const CustomerSignUp = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    city: '',
    country: 'Pakistan',
    currency: 'PKR',
    postal_code: ''
  });
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { signUp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get the path the user was trying to access before signup
  const from = location.state?.from || '/menu';

  // Load countries and cities on component mount
  useEffect(() => {
    loadCountries();
    loadCities(formData.country);
  }, []);

  // Load cities when country changes
  useEffect(() => {
    if (formData.country) {
      loadCities(formData.country);
      // Reset city when country changes
      setFormData(prev => ({ ...prev, city: '' }));
      
      // Update currency based on country
      const selectedCountry = countries.find(c => c.name === formData.country);
      if (selectedCountry) {
        setFormData(prev => ({ ...prev, currency: selectedCountry.currency }));
      }
    }
  }, [formData.country, countries]);

  const loadCountries = async () => {
    try {
      setLoadingLocations(true);
      const countriesData = await locationService.getCountries();
      setCountries(countriesData);
    } catch (error) {
      console.error('Error loading countries:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const loadCities = async (countryName) => {
    if (!countryName) return;
    
    try {
      setLoadingLocations(true);
      const citiesData = await locationService.getCitiesByCountry(countryName);
      setCities(citiesData);
    } catch (error) {
      console.error('Error loading cities:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-set currency when country is selected
    if (name === 'country' && value) {
      const selectedCountry = countries.find(c => c.name === value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        currency: selectedCountry?.currency || 'PKR'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const result = await signUp(formData.email, formData.password, {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        country: formData.country,
        currency: formData.currency,
        postal_code: formData.postal_code
      });

      if (result.success) {
        setSuccess('Account created successfully! You are now signed in.');
        setTimeout(() => {
          // Redirect to the page user was trying to access, or default to menu
          navigate(from, { replace: true });
        }, 2000);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="https://akuoggticrcsctgkerfl.supabase.co/storage/v1/object/public/public-images/Screenshot_2025-11-20_005401__1_-removebg-preview.png" alt="Company logo" width="60" height="60" />
          </div>
          <h1 className="auth-title">Create Account</h1>
          <p className="auth-subtitle">Create your account to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">
              <span className="error-icon">❌</span>
              {error}
            </div>
          )}

          {success && (
            <div className="auth-success">
              <span className="success-icon">✅</span>
              {success}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Enter your phone number"
            />
          </div>

          <div className="form-group">
            <label htmlFor="country">Country <span style={{color: 'red'}}>*</span></label>
            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '16px',
                fontFamily: 'inherit',
                background: '#f9fafb',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.outline = 'none';
                e.target.style.borderColor = '#667eea';
                e.target.style.background = 'white';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.background = '#f9fafb';
                e.target.style.boxShadow = 'none';
              }}
              required
              disabled={loadingLocations}
            >
              {countries.map((country) => (
                <option key={country.code} value={country.name}>
                  {country.name} ({country.currency_symbol} {country.currency})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="city">City <span style={{color: 'red'}}>*</span></label>
            <select
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '16px',
                fontFamily: 'inherit',
                background: '#f9fafb',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.outline = 'none';
                e.target.style.borderColor = '#667eea';
                e.target.style.background = 'white';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.background = '#f9fafb';
                e.target.style.boxShadow = 'none';
              }}
              required
              disabled={loadingLocations || cities.length === 0}
            >
              <option value="">Select your city</option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
            {loadingLocations && (
              <small style={{color: '#666', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                Loading cities...
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="postal_code">Postal Code</label>
            <input
              type="text"
              id="postal_code"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleInputChange}
              placeholder="Enter your postal/ZIP code"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '16px',
                fontFamily: 'inherit',
                background: '#f9fafb',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.outline = 'none';
                e.target.style.borderColor = '#667eea';
                e.target.style.background = 'white';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.background = '#f9fafb';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="currency">Currency (Auto-selected based on country)</label>
            <input
              type="text"
              id="currency"
              name="currency"
              value={formData.currency}
              readOnly
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '16px',
                fontFamily: 'inherit',
                background: '#f0f9ff',
                color: '#1e40af',
                fontWeight: '500',
                cursor: 'not-allowed',
                boxSizing: 'border-box'
              }}
            />
            <small style={{color: '#666', fontSize: '12px', marginTop: '4px', display: 'block'}}>
              Currency is automatically set based on your selected country
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="address">Delivery Address <span style={{color: 'red'}}>*</span></label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter your complete delivery address (e.g., House 123, Block A, Gulshan-e-Iqbal)"
              rows="3"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: '16px',
                fontFamily: 'inherit',
                resize: 'vertical',
                background: '#f9fafb',
                transition: 'all 0.3s ease',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => {
                e.target.style.outline = 'none';
                e.target.style.borderColor = '#667eea';
                e.target.style.background = 'white';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.background = '#f9fafb';
                e.target.style.boxShadow = 'none';
              }}
              required
            />
            <small style={{color: '#666', fontSize: '12px', marginTop: '4px', display: 'block'}}>
              💡 Use complete address with area name for accurate delivery
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Create a password"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm your password"
              required
            />
          </div>

          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account? 
            <Link to="/login" className="auth-link"> Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerSignUp;
